/** @jsxImportSource @emotion/react */

import type { ReactElement, ReactNode } from 'react';
import { cx } from '@emotion/css';
import { lightenBy, setAlpha } from '../../utils/color-utils';
import type { IconName } from '../Icon';
import Icon from '../Icon';
import ODSInfo from '../ODSInfo';

type Props = {
  /** Pass in an icon name if this is an empty view */
  icon?: IconName | ReactNode;
  /** What is this empty state all about */
  title?: ReactNode;
  /** If there anything else the user should know */
  description?: ReactNode;
  /** Pass in a call to action button */
  cta?: ReactNode;
  className?: string;
  /** How big do you want the icon & text to be: **lg** for empty views, **md** for sections and **sm** for panels */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** Don't want the main icon to be blue, is this an error, pass in danger */
  iconColor?: 'primary' | 'white' | 'secondary' | 'black' | 'danger' | 'success' | 'warning';
  /** Change the color of the text pass in the color name */
  textColor?: string;
  /** If there's a help tip pass in a helper label like "Learn how" */
  helperLabel?: ReactNode;
  /** Pass in the message for the help tip */
  helperMessage?: ReactNode;
  /** What's the title of the help tip */
  helperTitle?: ReactNode;
  /** Translucent icon background */
  translucent?: boolean;
};

function ODSEmptyState({
  icon,
  title,
  description,
  cta,
  className,
  size = 'md',
  iconColor = 'primary',
  textColor = 'secondary',
  helperLabel,
  helperMessage,
  helperTitle,
  translucent,
  ...divProps
}: Props): ReactElement {
  let iconWidth = '10rem';
  let fontSize = '1rem';
  let gap = 'gap-3';
  let descriptionSize = '0.75em';

  switch (size) {
    case 'sm':
      iconWidth = '8rem';
      fontSize = '0.85rem';
      descriptionSize = '0.75rem';
      gap = 'gap-2';
      break;
    case 'lg':
      iconWidth = '12.5rem';
      fontSize = '1.25rem';
      break;
    case 'xs':
      iconWidth = '6rem';
      fontSize = '0.75rem';
      descriptionSize = '0.75rem';
      gap = 'gap-2';
      break;
    default:
  }

  return (
    <div
      {...divProps}
      className={cx(
        'd-flex flex-column align-items-center justify-content-center w-100 h-100 text-center user-select-none',
        `text-${textColor}`,
        gap,
        className
      )}
      css={{ fontSize }}
    >
      {icon && (
        <div
          className="mb-1"
          css={{ width: iconWidth }}
          style={{ '--aspect-ratio': 1 } as React.CSSProperties}
        >
          <div
            className={`d-flex justify-content-center align-items-center rounded-circle text-${iconColor}`}
            css={(theme) => ({
              padding: '25%',
              backgroundColor: (translucent
                ? setAlpha(theme.colors[iconColor], 0.1)
                : lightenBy(theme.colors[iconColor], 0.9)
              ).toString(),
            })}
          >
            {typeof icon === 'string' && (
              <Icon className="w-50 h-50" name={icon as IconName} fixedWidth />
            )}
            {typeof icon !== 'string' && icon}
          </div>
        </div>
      )}
      {!!title && (
        <div className="empty-state-title" css={{ fontSize: '1em' }}>
          {title}
        </div>
      )}
      {!!description && (
        <div
          css={{ fontSize: descriptionSize }}
          className={cx('empty-state-description', { 'fst-italic': !title })}
        >
          {description}
        </div>
      )}
      {!!cta && <div>{cta}</div>}
      {!!helperMessage && (
        <ODSInfo message={helperMessage} title={helperTitle}>
          {helperLabel}
        </ODSInfo>
      )}
    </div>
  );
}

export default ODSEmptyState;
